import { Component, ChangeDetectorRef, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormComponent } from '../../shared/components/form/form.component';
import type { NgxForm } from '../../shared/components/form/ngx-form.types';
import { RagDatasupplyFrontendService } from '@reflact/rag-datasupply';
import { GlobalFrontendUser, Permissions } from '@reflact/reflactportal';
import { ObjectId } from 'bson';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { OverlayService } from 'src/app/shared/services/overlay.service';
import { MandantUserDetail, UserService } from 'src/app/shared/services/user.service';
import { TileService } from 'src/app/shared/services/tile.service';
import { Tile } from 'src/app/shared/types/tile.types';
import { Mandant } from 'src/app/shared/types/mandant.types';
import { UserManagementComponent } from 'src/app/shared/components/user-management/user-management.component';

/** MenuItem des "+"-Dropdowns für "Zu Mandant hinzufügen" — transportiert den vollen Mandanten
 * fürs Label, siehe getMandantMenuItems(). */
interface MandantMenuItem extends MenuItem {
  mandant: Mandant;
}

@Component({
  selector: 'app-global-user-administration',
  templateUrl: './global-user-administration.component.html',
  styleUrls: ['./global-user-administration.component.scss'],
  providers: [RagDatasupplyFrontendService],
  standalone: false
})
export class GlobalUserAdministrationComponent implements OnInit {
  @Input() public userId?: string;
  @Output() public userSaved = new EventEmitter<void>();
  @ViewChild('embeddedForm') public embeddedForm?: FormComponent;
  public embeddedUser?: GlobalFrontendUser;
  public embeddedLoading = false;
  public embeddedSaving = false;
  public creating = false;
  public embeddedError = '';
  private embeddedSnapshot = '';
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(UserManagementComponent) public userManagement?: UserManagementComponent<GlobalFrontendUser>;
  public mandants: Mandant[] = [];
  public allTiles: Tile[] = [];
  public translatedLabels: NgxForm.TranslatedLabels = new Map<string, string>([
    ["email", $localize`:@@globalEmail:Email`],
    ["name", $localize`:@@globalName:Name`],
    ["reflactadmin", $localize`:@@globalUserAdminReflactAdmin:Reflact Admin`],
    ["permissions", $localize`:@@globalUserAdminPermissions:Berechtigungen`],
    ["mandant", $localize`:@@globalMandant:Mandant`],
    ["mandantSelection", $localize`:@@globalUserAdminMandantSelection:Mandant Selektierung`],
  ])
  /** Tooltip-Text für die Mandanten-Zähler-Spalte, wenn der Nutzer keinem Mandanten angehört. */
  public globalUserNoMandants = $localize`:@@globalUserNoMandants:Keinem Mandanten zugewiesen`;
  public readonly internalLabel = $localize`:@@layoutInternalBadge:Intern`;
  constructor(public frontendService: RagDatasupplyFrontendService<GlobalFrontendUser>, private userService: UserService, private toastrService: ToastrService, public mandantService: MandantService, private tileService: TileService, private overlayService: OverlayService, private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    if (this.userId) {
      void this.loadEmbeddedUser();
      return;
    }
    this.userService.getGlobalUsers().then(u => {
      const sorted = [...u].sort((a, b) => a.email.localeCompare(b.email));
      this.frontendService.fromArray(sorted);
      const selectedId = this.overlayService.get("selected");
      const selected = sorted.find(x => x._id.toString() == selectedId);
      if (selected) this.onSelectGlobalUser(selected);
    });
    this.mandantService.getMandants().then(m => {
      this.mandants = m.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.tileService.listTiles().then(t => this.allTiles = t);
  }

  public async loadEmbeddedUser(): Promise<void> {
    this.embeddedLoading = true;
    this.embeddedError = '';
    try {
      const [users, mandants, tiles] = await Promise.all([
        this.userService.getGlobalUsers(), this.mandantService.getMandants(), this.tileService.listTiles()
      ]);
      if (this.destroyRef.destroyed) return;
      const user = users.find(candidate => candidate._id.toString() === this.userId);
      if (!user) throw new Error('Nutzer nicht gefunden.');
      this.mandants = [...mandants].sort((first, second) => first.name.localeCompare(second.name));
      this.allTiles = tiles;
      this.embeddedUser = { ...user };
      await this.onSelectGlobalUser(this.embeddedUser);
      this.embeddedSnapshot = JSON.stringify(this.embeddedUser);
    } catch {
      this.embeddedUser = undefined;
      this.embeddedError = $localize`:@@globalUserLoadFailed:Der Nutzer konnte nicht geladen werden.`;
    } finally {
      if (!this.destroyRef.destroyed) {
        this.embeddedLoading = false;
        this.cdr.detectChanges();
      }
    }
  }

  public hasEmbeddedChanges(): boolean {
    return !!this.embeddedUser && JSON.stringify(this.embeddedUser) !== this.embeddedSnapshot;
  }

  public async saveEmbeddedUser(data: GlobalFrontendUser): Promise<void> {
    if (this.embeddedSaving || this.embeddedLoading) return;
    this.embeddedSaving = true;
    try {
      if (await this.performUpdateUser(data)) {
        this.embeddedSnapshot = JSON.stringify(data);
        this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
        this.userSaved.emit();
      }
    } catch {
      this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
    } finally {
      if (!this.destroyRef.destroyed) {
        this.embeddedSaving = false;
        this.cdr.detectChanges();
      }
    }
  }

  public getTileById(tileId: string): Tile | undefined {
    return this.allTiles.find(t => t._id === tileId);
  }


  /** Populates `data.mandants` (missing from the Logto-backed GlobalFrontendUser) so the
   * "Zu Mandant hinzufügen" panel can show/filter the user's current mandant memberships, sowie
    * `data.mandantDetails` (Rollen + persönliche Kachelfreigaben je Mandant, siehe Super-Administration). */
  public async onSelectGlobalUser(data: GlobalFrontendUser) {
    const res = await this.userService.getMandantsForUser(data._id.toString());
    if (this.destroyRef.destroyed) return;
    if (this.userId && res.status !== 'ok') throw new Error('Mandanten konnten nicht geladen werden.');
    if (res.status == "ok") {
      (data as any).mandants = res.mandants.map(m => m._id);
    }
    const detailsRes = await this.userService.getMandantDetailsForUser(data._id.toString());
    if (this.destroyRef.destroyed) return;
    if (this.userId && detailsRes.status !== 'ok') throw new Error('Nutzerrechte konnten nicht geladen werden.');
    if (detailsRes.status == "ok") {
      (data as any).mandantDetails = detailsRes.mandants;
    }
    // Ausgangsstand für den Speichern-Diff (siehe saveMandantChanges()) — Mandanten-Zuweisung,
    // Berechtigungen und Kachelfreigaben werden ab jetzt nur noch lokal verändert und erst beim
    // Klick auf "Speichern" tatsächlich persistiert.
    this.captureMandantStateSnapshot(data);
    // Der Speichern-Button vergleicht editUser gegen einen beim Öffnen eingefrorenen Snapshot —
    // der wurde VOR diesem async Nachladen erfasst, also ohne mandants/mandantDetails. Ohne
    // dieses Neu-Einfrieren würde "Speichern" sofort als aktiv angezeigt, ohne dass der Nutzer
    // etwas geändert hat.
    this.userManagement?.refreshEditUserSnapshot();
    // Async-Fortsetzungen nach `await` lösen in dieser App keine automatische Change Detection
    // aus (bekanntes Muster) — ohne diesen Aufruf bleibt z.B. die Mandanten-Liste im Drawer
    // optisch auf dem alten Stand, obwohl `data.mandants`/`data.mandantDetails` schon aktuell sind.
    this.cdr.detectChanges();
  }

  /** Als Arrow-Function-Property (nicht Methode), damit `this` beim Aufruf über
   * `<app-user-management [saveUser]="performUpdateUser">` erhalten bleibt — der Kind-Komponente
   * gibt das zurückgegebene Bool Feedback direkt am Speichern-Button statt per Toastr. */
  public performUpdateUser = async (data: GlobalFrontendUser): Promise<boolean> => {
    // Guards against a stray submit reaching here with a stale/incomplete object.
    if (!data?._id) return false;
    const res = await this.userService.editGlobalUser(data._id.toString(), data.email, data.enableAuthentificator, data.name, data.reflactadmin);
    if (res.status !== "ok") {
      this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
      return false;
    }
    // Erst hier werden die seit dem Öffnen lokal vorgenommenen Änderungen an Mandanten-
    // Zuweisung/Berechtigungen/Kacheln tatsächlich gespeichert (siehe saveMandantChanges()).
    if (!await this.saveMandantChanges(data)) {
      this.toastrService.error($localize`:@@globalUserRightsSaveFailed:Die Nutzerrechte konnten nicht vollständig gespeichert werden. Bitte versuche es erneut.`);
      return false;
    }
    if (!this.userId) this.userService.getGlobalUsers().then(u => this.frontendService.fromArray(u))
    return true;
  }

  public async createUser(data: any) {
    // Guards against a stray duplicate (createUserEvent) firing with empty data alongside the
    // real submit (see FormComponent#submitLock) — silently drops it instead of sending a
    // request that's guaranteed to fail validation on the backend.
    if (!data?.email || !data?.name || !data?.mandant) return;
    this.creating = true;
    this.cdr.detectChanges();
    let res;
    try {
      res = await this.userService.createUserAsReflactAdmin(data.email, data.name, data.permissions ?? [], data.mandant, data.reflactadmin, []);
    } finally {
      this.creating = false;
      this.cdr.detectChanges();
    }
    if (res.status == "ok") {
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
    }
    this.userService.getGlobalUsers().then(u => this.frontendService.fromArray(u))
  }

  public arrayContains(data: any[] | undefined, element: ObjectId) {
    return (data ?? []).find((id: string) => id == element.toString()) != undefined
  }

  /** Mandanten, denen der Nutzer noch nicht zugewiesen ist — Quelle f\u00fcrs "+"-Dropdown. Gecacht
   * pro `data`-Objekt (WeakMap): `p-menu` ist OnPush, eine bei jedem Change-Detection-Zyklus neu
   * erzeugte `[model]`-Array-Referenz w\u00fcrde das offene Men\u00fc mitten im Klick neu rendern (siehe
   * mandant-administration.component.ts#getAvailableTilesToAdd f\u00fcr denselben Fix). */
  private availableMandantsCache = new WeakMap<object, { mandantsRef: Mandant[]; dataMandantsRef: any[]; result: Mandant[] }>();

  public getAvailableMandantsToAdd(data: any): Mandant[] {
    const dataMandants = data.mandants ?? [];
    const cached = this.availableMandantsCache.get(data);
    if (cached && cached.mandantsRef === this.mandants && cached.dataMandantsRef === dataMandants) {
      return cached.result;
    }
    const result = this.mandants.filter(m => !this.arrayContains(dataMandants, m._id));
    this.availableMandantsCache.set(data, { mandantsRef: this.mandants, dataMandantsRef: dataMandants, result });
    return result;
  }

  private mandantMenuItemsCache = new WeakMap<object, { sourceRef: Mandant[]; items: MandantMenuItem[] }>();

  public getMandantMenuItems(data: any): MandantMenuItem[] {
    const available = this.getAvailableMandantsToAdd(data);
    const cached = this.mandantMenuItemsCache.get(data);
    if (cached && cached.sourceRef === available) {
      return cached.items;
    }
    const items: MandantMenuItem[] = available.map(mandant => ({
      label: mandant.name,
      badge: mandant.internal ? this.internalLabel : undefined,
      badgeStyleClass: 'internal-menu-badge',
      mandant,
      command: () => this.addUserToMandant(data, mandant._id.toString()),
    }));
    this.mandantMenuItemsCache.set(data, { sourceRef: available, items });
    return items;
  }

  /** Nur noch lokale Mutation — Mandanten-Zuweisung wird erst beim Klick auf "Speichern"
   * tatsächlich persistiert, siehe saveMandantChanges(). */
  public addUserToMandant(data: any, mandantId: string): void {
    const mandants: string[] = data.mandants ?? [];
    if (mandants.includes(mandantId)) return;
    data.mandants = [...mandants, mandantId];
    const details: MandantUserDetail[] = data.mandantDetails ?? [];
    if (!details.some(d => d._id === mandantId)) {
      const mandant = this.mandants.find(m => m._id.toString() === mandantId);
      data.mandantDetails = [...details, { _id: mandantId, name: mandant?.name ?? '', permissions: [], tiles: [], tilePermissions: {} }];
    }
  }

  /** Nur noch lokale Mutation — siehe addUserToMandant(). */
  public removeUserFromMandant(data: any, mandantId: string): void {
    data.mandants = ((data.mandants ?? []) as string[]).filter(m => m !== mandantId);
    data.mandantDetails = ((data.mandantDetails ?? []) as MandantUserDetail[]).filter(d => d._id !== mandantId);
  }

  /** Snapshot von mandants/mandantDetails beim Öffnen bzw. nach dem letzten Speichern — Grundlage
   * für den Diff in saveMandantChanges(). Tiefenkopie nötig, da permissions/tiles Arrays lokal
   * weitermutiert werden. */
  private mandantStateSnapshots = new WeakMap<object, { mandants: string[]; mandantDetails: MandantUserDetail[] }>();

  private captureMandantStateSnapshot(data: any): void {
    this.mandantStateSnapshots.set(data, {
      mandants: [...(data.mandants ?? [])],
      mandantDetails: ((data.mandantDetails ?? []) as MandantUserDetail[]).map(d => ({
        ...d,
        permissions: [...d.permissions],
        tiles: [...d.tiles],
        tilePermissions: Object.fromEntries(Object.entries(d.tilePermissions ?? {}).map(([tileId, keys]) => [tileId, [...keys]])),
      })),
    });
  }

  /** `p-picklist` mutiert bei Button-Klicks (moveRight/moveLeft/…) source/target in-place statt
   * über das Model-Signal zu schreiben — `(targetChange)` feuert dabei nie (siehe
   * form.component.ts#syncPicklistValue für dieselbe Erklärung an einer anderen Stelle). Deshalb
   * hier ebenfalls auf die tatsächlich feuernden `onMove*`-Events umgestellt, rein lokal (kein
   * sofortiges Speichern mehr), siehe saveMandantChanges(). */
  /** Vereinfachtes Rollenmodell: nur noch "admin" (Mandanten-Admin) als zuweisbare Rolle - siehe
   * docs/mandanten-rollen-rechte.md. Andere, hier nicht verwaltete Rollen (z.B. "kitools-admin",
   * von anderen Apps über ihr eigenes Token gelesen) bleiben beim Toggeln unangetastet. */
  public isMandantAdmin(detail: MandantUserDetail): boolean {
    return detail.permissions.includes('admin');
  }

  public toggleMandantAdmin(detail: MandantUserDetail, checked: boolean): void {
    detail.permissions = checked
      ? [...detail.permissions.filter(p => p !== 'admin'), 'admin']
      : detail.permissions.filter(p => p !== 'admin');
  }

  /** Für die Accordion-Kopfzeile (Badge neben dem Mandantennamen, auch ohne Aufklappen sichtbar). */
  public isMandantAdminInMandant(data: { mandantDetails?: MandantUserDetail[] }, mandantId: string): boolean {
    return data.mandantDetails?.find(d => d._id === mandantId)?.permissions.includes('admin') ?? false;
  }

  private assignableTilesCache = new Map<string, { allTilesRef: Tile[]; poolRef: string[]; activeTilesKey: string; result: Tile[] }>();

  public getPermissionTilesForMandant(mandantId: string): Tile[] {
    const mandant = this.mandantService.findMandant(mandantId as unknown as ObjectId);
    const poolTileIds = (mandant?.tiles ?? []) as unknown as string[];
    return this.allTiles.filter(tile => poolTileIds.includes(tile._id) && !!tile.customPermissions?.length);
  }

  public getAssignableTilesForMandant(mandantId: string): Tile[] {
    const mandant = this.mandantService.findMandant(mandantId as unknown as ObjectId);
    const poolTileIds = (mandant?.tiles ?? []) as unknown as string[];
    const activeTileIds = mandant?.activeTiles ?? [];
    const activeTilesKey = JSON.stringify(activeTileIds);
    const cached = this.assignableTilesCache.get(mandantId);
    if (cached && cached.allTilesRef === this.allTiles && cached.poolRef === poolTileIds && cached.activeTilesKey === activeTilesKey) {
      return cached.result;
    }
    const result = this.allTiles.filter(tile => poolTileIds.includes(tile._id) && !activeTileIds.includes(tile._id));
    this.assignableTilesCache.set(mandantId, { allTilesRef: this.allTiles, poolRef: poolTileIds, activeTilesKey, result });
    return result;
  }

  private tilesPicklistCache = new WeakMap<object, { availableRef: Tile[]; contextKey: string; readonlyTiles: Tile[]; source: Tile[]; target: Tile[] }>();

  private getTilesPicklistState(mandantId: string, detail: MandantUserDetail) {
    const available = this.getAssignableTilesForMandant(mandantId);
    const mandant = this.mandantService.findMandant(mandantId as unknown as ObjectId);
    const pool = (mandant?.tiles ?? []) as unknown as string[];
    const shared = this.allTiles.filter(tile => pool.includes(tile._id) && mandant?.activeTiles.includes(tile._id)
      && ((tile.permissions ?? []).every(permission => detail.permissions.includes(permission)) || detail.tiles.includes(tile._id)));
    const contextKey = JSON.stringify([mandantId, detail.permissions, shared.map(tile => tile._id)]);
    const cached = this.tilesPicklistCache.get(detail);
    if (cached && cached.availableRef === available && cached.contextKey === contextKey) {
      return cached;
    }
    const readonlyTiles = shared.map(tile => ({ ...tile, readonlyAssignment: true }));
    const state = {
      availableRef: available,
      contextKey,
      readonlyTiles,
      source: available.filter(t => !detail.tiles.includes(t._id)),
      target: [...readonlyTiles, ...available.filter(t => detail.tiles.includes(t._id))],
    };
    this.tilesPicklistCache.set(detail, state);
    return state;
  }

  public getTilesPicklistSource(mandantId: string, detail: MandantUserDetail): Tile[] {
    return this.getTilesPicklistState(mandantId, detail).source;
  }

  public getTilesPicklistTarget(mandantId: string, detail: MandantUserDetail): Tile[] {
    return this.getTilesPicklistState(mandantId, detail).target;
  }

  public syncTilesPicklistValue(mandantId: string, detail: MandantUserDetail): void {
    const state = this.getTilesPicklistState(mandantId, detail);
    const readonlyIds = new Set(state.readonlyTiles.map(tile => tile._id));
    state.source.splice(0, state.source.length, ...state.source.filter(tile => !readonlyIds.has(tile._id)));
    state.target.splice(0, state.target.length, ...state.readonlyTiles, ...state.target.filter(tile => !readonlyIds.has(tile._id)));
    const editableTileIds = new Set(state.availableRef.map(tile => tile._id));
    const preservedTileIds = detail.tiles.filter(tileId => !editableTileIds.has(tileId));
    detail.tiles = [...preservedTileIds, ...state.target.filter(tile => !readonlyIds.has(tile._id)).map(tile => tile._id)];
  }

  /** Ob/wie ein zugewiesenes Kachel-Zusatzrecht (`tile.customPermissions`) aktuell für diesen
   * Nutzer/Mandanten gesetzt ist — Basis für die Checkboxen unter der Kacheln-Picklist. */
  public hasTilePermission(detail: MandantUserDetail, tileId: string, key: string): boolean {
    return (detail.tilePermissions?.[tileId] ?? []).includes(key);
  }

  public toggleTilePermission(detail: MandantUserDetail, tileId: string, key: string): void {
    detail.tilePermissions ??= {};
    const current = detail.tilePermissions[tileId] ?? [];
    detail.tilePermissions[tileId] = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
  }


  private async saveMandantChanges(data: any): Promise<boolean> {
    const before = this.mandantStateSnapshots.get(data);
    const afterMandants: string[] = data.mandants ?? [];
    const afterDetails: MandantUserDetail[] = data.mandantDetails ?? [];
    const beforeMandants = before?.mandants ?? [];
    const beforeDetails = before?.mandantDetails ?? [];

    const added = afterMandants.filter(m => !beforeMandants.includes(m));
    const removed = beforeMandants.filter(m => !afterMandants.includes(m));

    for (const mandantId of removed) {
      if ((await this.userService.deleteMandantUserAsReflactAdmin(data._id, mandantId)).status !== 'ok') return false;
    }
    for (const mandantId of added) {
      const detail = afterDetails.find(d => d._id === mandantId);
      const permissions = (detail?.permissions as Permissions[] | undefined) ?? (data.reflactadmin ? ["admin"] : []);
      if ((await this.userService.createUserAsReflactAdmin(data.email, data.name, permissions, mandantId, data.reflactadmin, [])).status !== 'ok') return false;
      if (detail?.tiles?.length || detail?.tilePermissions && Object.keys(detail.tilePermissions).length) {
        if ((await this.userService.setMandantUserTilesAsReflactAdmin(mandantId, data._id.toString(), detail?.tiles ?? [], detail?.tilePermissions)).status !== 'ok') return false;
      }
    }
    for (const detail of afterDetails) {
      if (added.includes(detail._id) || removed.includes(detail._id)) continue;
      const beforeDetail = beforeDetails.find(d => d._id === detail._id);
      const permissionsChanged = JSON.stringify([...detail.permissions].sort()) !== JSON.stringify([...(beforeDetail?.permissions ?? [])].sort());
      const tilesChanged = JSON.stringify([...detail.tiles].sort()) !== JSON.stringify([...(beforeDetail?.tiles ?? [])].sort());
      const tilePermissionsChanged = JSON.stringify(detail.tilePermissions ?? {}) !== JSON.stringify(beforeDetail?.tilePermissions ?? {});
      if (permissionsChanged) {
        if ((await this.userService.editMandantUserAsReflactAdmin(detail._id, data._id.toString(), detail.permissions as Permissions[], [])).status !== 'ok') return false;
      }
      if (tilesChanged || tilePermissionsChanged) {
        if ((await this.userService.setMandantUserTilesAsReflactAdmin(detail._id, data._id.toString(), detail.tiles, detail.tilePermissions)).status !== 'ok') return false;
      }
    }
    this.captureMandantStateSnapshot(data);
    return true;
  }

}
